import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import commonEn from './locales/en/common.json'
import commonRu from './locales/ru/common.json'

export const LOCALES = ['en', 'ru'] as const
export type Locale = (typeof LOCALES)[number]

/**
 * The language anything falls back to — `fallbackLng` below, named so code that
 * has to answer "which locale" before i18n has resolved one is not guessing.
 */
export const FALLBACK_LOCALE: Locale = 'ru'

const LOCALE_STORAGE_KEY = 'ui-locale'

/**
 * `common` holds strings reused everywhere — actions, statuses, validation,
 * error codes. A feature owns its own namespace and registers it itself, so
 * this file never has to know features exist.
 */
void i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: commonEn },
      ru: { common: commonRu },
    },
    supportedLngs: LOCALES,
    fallbackLng: FALLBACK_LOCALE,
    defaultNS: 'common',
    /*
     * The interface language is Russian, so the browser is not asked. Reading
     * `navigator` here made an en-US browser render the panel in English while
     * every kit default and every untranslated corner stayed Russian — one
     * screen in two languages. English stays reachable through `setLocale`,
     * which writes the choice to localStorage and is what this reads back.
     */
    detection: {
      order: ['localStorage'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      // Nothing is written here. The detector's own cache would persist a
      // *detected* language as if the user had picked it, and that stored
      // value then outlives any change to the default. Only `setLocale` writes.
      caches: [],
    },
    interpolation: { escapeValue: false }, // React escapes already
  })

/** Features call this at module scope to add their namespace. */
export function registerNamespace(
  namespace: string,
  bundles: Partial<Record<Locale, Record<string, unknown>>>,
) {
  for (const [locale, resources] of Object.entries(bundles)) {
    i18next.addResourceBundle(locale, namespace, resources, true, true)
  }
}

/**
 * Keeps `<html lang>` telling the truth.
 *
 * The attribute is what a screen reader picks a voice and a pronunciation
 * dictionary from, so a Russian panel labelled `en` is read out in an English
 * accent. `index.html` sets it before first paint from the same storage key;
 * this is the other half, for when the language changes without a reload.
 */
const applyDocumentLanguage = (locale: string) => {
  // This module is imported by formatters that are unit-tested in a Node
  // environment, where there is no document to name.
  if (typeof document === 'undefined') return
  document.documentElement.lang = locale.startsWith('en') ? 'en' : 'ru'
}

applyDocumentLanguage(i18next.resolvedLanguage ?? FALLBACK_LOCALE)

/** The one place a language choice is persisted — see `detection.caches`. */
export const setLocale = async (locale: Locale) => {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  await i18next.changeLanguage(locale)
  applyDocumentLanguage(locale)
}

export const i18n = i18next
